import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Bar, XAxis, YAxis, Tooltip, Legend, Line, ComposedChart, Cell, ResponsiveContainer } from 'recharts';

/** API item shape from recruitment-retention endpoint */
export interface IRetentionApiItem {
  currentEndDate?: string;
  rebooked?: string;
  [key: string]: unknown;
}

const COLORS = {
  booked: '#1C6BC3',
  rebooked: '#379ADF',
  retentionLine: '#9C27B0',
};

const MONTH_ORDER = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];

/** Parse MM/DD/YYYY from API */
function parseDate(s: string | undefined): Date | null {
  if (!s || typeof s !== 'string') return null;
  const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (!m) return null;
  const [, month, day, year] = m;
  const d = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  return isNaN(d.getTime()) ? null : d;
}

/** Build bar chart data from API items. FY = Apr 1 - Mar 31. */
function buildChartData(items: IRetentionApiItem[], fyStartYear: number) {
  const fyStart = new Date(fyStartYear, 3, 1);
  const fyEnd = new Date(fyStartYear + 1, 2, 31, 23, 59, 59, 999);
  const byMonth: Record<string, { booked: number; rebooked: number }> = {};
  MONTH_ORDER.forEach(m => (byMonth[m] = { booked: 0, rebooked: 0 }));

  items.forEach(item => {
    const raw = item as Record<string, unknown>;
    const endDateStr = raw.currentEndDate ?? raw.CurrentEndDate ?? item.currentEndDate;
    const endDate = parseDate(endDateStr as string);
    if (!endDate || endDate < fyStart || endDate > fyEnd) return;

    const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthLabel = monthLabels[endDate.getMonth()];
    const rebookedVal = raw.rebooked ?? raw.Rebooked ?? item.rebooked;
    const isRebooked = String(rebookedVal ?? '').toUpperCase() === 'Y';

    if (byMonth[monthLabel]) {
      if (isRebooked) byMonth[monthLabel].rebooked += 1;
      else byMonth[monthLabel].booked += 1;
    }
  });

  return MONTH_ORDER.map(month => {
    const { booked, rebooked } = byMonth[month];
    const total = booked + rebooked;
    return {
      month,
      booked,
      rebooked,
      total,
      retentionPercent: total > 0 ? (rebooked / total) * 100 : 0,
      retentionY: total > 0 ? rebooked : 0,
    };
  });
}

const useStyles = makeStyles()(theme => ({
  chartBox: {
    width: '100%',
    height: 280,
    minHeight: 280,
  },
}));

export interface IRetentionBarChartProps {
  items: IRetentionApiItem[];
  fyStartYear: number;
  selectedMonth?: string | null;
  onMonthClick?: (month: string) => void;
}

export const RetentionBarChart: React.FC<IRetentionBarChartProps> = ({
  items,
  fyStartYear,
  selectedMonth,
  onMonthClick,
}) => {
  const { classes } = useStyles();
  const data = React.useMemo(() => buildChartData(items, fyStartYear), [items, fyStartYear]);

  return (
    <Box className={classes.chartBox}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }} barCategoryGap="20%">
        <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#333" />
        <YAxis yAxisId="left" hide allowDecimals={false} domain={[0, 'auto']} />
        <Tooltip
          content={({ active, payload, label }) => {
            if (!active || !payload?.length || !label) return null;
            const d = payload[0]?.payload as typeof data[0];
            return (
              <div style={{ padding: 8, background: 'white', border: '1px solid #ccc', borderRadius: 4, fontSize: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
                <div>Rebooked: {d?.rebooked ?? 0}</div>
                <div>Booked: {d?.booked ?? 0}</div>
                <div>Retention %: {(d?.retentionPercent ?? 0).toFixed(2)}</div>
              </div>
            );
          }}
        />
        <Legend verticalAlign="top" align="right" wrapperStyle={{ paddingBottom: 8 }} />
        <Bar
          yAxisId="left"
          dataKey="rebooked"
          name="Rebooked"
          stackId="a"
          fill={COLORS.rebooked}
          minPointSize={2}
          onClick={onMonthClick ? (e: any) => e?.payload?.month && onMonthClick(e.payload.month) : undefined}
          cursor={onMonthClick ? 'pointer' : undefined}
        >
          {data.map((entry, idx) => (
            <Cell
              key={idx}
              fill={
                !selectedMonth || entry.month === selectedMonth ? COLORS.rebooked : '#E0E0E0'
              }
            />
          ))}
        </Bar>
        <Bar
          yAxisId="left"
          dataKey="booked"
          name="Booked"
          stackId="a"
          fill={COLORS.booked}
          minPointSize={2}
          onClick={onMonthClick ? (e: any) => e?.payload?.month && onMonthClick(e.payload.month) : undefined}
          cursor={onMonthClick ? 'pointer' : undefined}
        >
          {data.map((entry, idx) => (
            <Cell
              key={idx}
              fill={
                !selectedMonth || entry.month === selectedMonth ? COLORS.booked : '#E0E0E0'
              }
            />
          ))}
        </Bar>
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="retentionY"
          name="Retention %"
          stroke={COLORS.retentionLine}
          strokeWidth={2}
          dot={{ r: 4 }}
          legendType="line"
        />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};
